/**
 * TanStack Query hooks for `advertiser-api-v2`. Thin wrappers over ./api.
 */
import {
	useMutation,
	useQuery,
	useQueryClient,
	type UseMutationResult,
	type UseQueryResult,
} from "@tanstack/react-query";
import {
	advertiserApiV2QueryKeys,
	createKey,
	listAccessLog,
	listKeys,
	revokeKey,
	type CreateKeyBody,
	type CreateKeyResponse,
	type ListAccessLogResponse,
	type ListKeysResponse,
	type RevokeKeyResponse,
} from "./api";

export function useAdvertiserApiV2List(
	advertiserId: string,
): UseQueryResult<ListKeysResponse, Error> {
	const enabled = advertiserId.trim().length > 0;
	return useQuery({
		queryKey: advertiserApiV2QueryKeys.keys(advertiserId),
		queryFn: () => listKeys(advertiserId),
		enabled,
	});
}

export function useAdvertiserApiV2Create(): UseMutationResult<
	CreateKeyResponse,
	Error,
	CreateKeyBody
> {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: createKey,
		onSuccess: () => {
			qc.invalidateQueries({ queryKey: advertiserApiV2QueryKeys.all });
		},
	});
}

export function useAdvertiserApiV2Delete(): UseMutationResult<
	RevokeKeyResponse,
	Error,
	string
> {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: revokeKey,
		onSuccess: () => {
			qc.invalidateQueries({ queryKey: advertiserApiV2QueryKeys.all });
		},
	});
}

export function useAdvertiserApiV2AccessLog(
	advertiserId: string,
	limit: number = 100,
): UseQueryResult<ListAccessLogResponse, Error> {
	const enabled = advertiserId.trim().length > 0;
	return useQuery({
		queryKey: advertiserApiV2QueryKeys.accessLog(advertiserId, limit),
		queryFn: () => listAccessLog(advertiserId, limit),
		enabled,
	});
}
